import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, MessageSquare, Shield, AlertCircle, ArrowUpRight, ArrowDownRight, ArrowRight, Zap } from 'lucide-react';
interface PatternCardProps {
  type: string;
  count: number;
  trend: 'up' | 'down' | 'stable';
  rank: number;
  delay?: number;
}
export function PatternCard({
  type,
  count,
  trend,
  rank,
  delay = 0
}: PatternCardProps) {
  const getIcon = (t: string) => {
    const lower = t.toLowerCase();
    if (lower.includes('gaslight')) return AlertTriangle;
    if (lower.includes('defens')) return Shield;
    if (lower.includes('aggress')) return AlertCircle;
    if (lower.includes('manipulat')) return Zap;
    return MessageSquare;
  };
  const Icon = getIcon(type);
  const getTrendConfig = () => {
    if (trend === 'up') return {
      icon: ArrowUpRight,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-500/20',
      label: 'Rising'
    };
    if (trend === 'down') return {
      icon: ArrowDownRight,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/20',
      label: 'Falling'
    };
    return {
      icon: ArrowRight,
      color: 'text-gray-400',
      bg: 'bg-gray-500/10',
      border: 'border-gray-500/20',
      label: 'Stable'
    };
  };
  const trendConfig = getTrendConfig();
  const TrendIcon = trendConfig.icon;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    delay,
    duration: 0.5,
    ease: 'easeOut'
  }} className="relative group h-full" data-id="element-1380" data-mp="3lyyo7scah5ra">
      {/* Hover Glow Effect */}
      <div className="absolute -inset-0.5 bg-gradient-to-br from-red-500/20 to-purple-500/20 rounded-3xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-1381" data-mp="1b9jox6l3f3gx" />

      <div className="relative h-full bg-gray-900/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-red-500/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-red-900/20 flex flex-col" data-id="element-1382" data-mp="1dswsqw5972ob">
        {/* Header */}
        <div className="flex items-start justify-between mb-6" data-id="element-1383" data-mp="zsvhqnv9yev4">
          <div className="relative" data-id="element-1384" data-mp="zugdggtbs3uk">
            <div className="absolute inset-0 bg-red-500/20 blur-lg rounded-full opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1385" data-mp="1xgnr0g7lx6j2" />
            <div className="relative w-12 h-12 flex items-center justify-center bg-gray-800 rounded-xl border border-white/10 text-red-400 group-hover:text-red-300 group-hover:border-red-500/30 transition-colors" data-id="element-1386" data-mp="1silxf1ck1otc">
              <Icon size={24} strokeWidth={2} data-id="element-1387" data-mp="1iqy9vbj4crx5" />
            </div>
          </div>

          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border ${trendConfig.bg} ${trendConfig.border}`} data-id="element-1388" data-mp="2g86usp9ggom5">
            <TrendIcon size={14} className={trendConfig.color} strokeWidth={2.5} data-id="element-1389" data-mp="2belswm4qraho" />
            <span className={`text-xs font-bold uppercase tracking-wide ${trendConfig.color}`} data-id="element-1390" data-mp="2nq4rft6jg57y">
              {trendConfig.label}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1" data-id="element-1391" data-mp="2xplwc8dyuf2v">
          <h3 className="text-xl font-bold text-white mb-1 group-hover:text-red-200 transition-colors" data-id="element-1392" data-mp="2xmhiejap84z0">
            {type}
          </h3>
          <p className="text-gray-400 text-sm font-medium mb-6" data-id="element-1393" data-mp="36oqrr5u8dsat">
            Detected <span className="text-white font-bold" data-id="element-1394" data-mp="3bg8vg1nn26zh">{count}</span> times
          </p>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-800/50 h-2 rounded-full overflow-hidden border border-white/5" data-id="element-1395" data-mp="3gflescfawvxb">
          <motion.div initial={{
          width: 0
        }} whileInView={{
          width: `${Math.min(count * 8, 100)}%`
        }} transition={{
          duration: 1.2,
          delay: delay + 0.3,
          ease: 'easeOut'
        }} className="h-full bg-gradient-to-r from-red-500 to-purple-500 rounded-full shadow-[0_0_10px_rgba(220,38,38,0.4)]" data-id="element-1396" data-mp="3lfg5aev5zjhu" />
        </div>

        {/* Rank Badge */}
        <div className="absolute -top-3 -right-3 w-8 h-8 bg-gray-900 border border-white/10 rounded-full flex items-center justify-center text-sm font-black text-white shadow-lg z-10 group-hover:scale-110 group-hover:border-red-500/50 transition-all" data-id="element-1397" data-mp="vpyx9jpgmsi7">
          <span className="text-transparent bg-clip-text bg-gradient-to-br from-white to-gray-400" data-id="element-1398" data-mp="33wb45dmk4yp">
            #{rank}
          </span>
        </div>
      </div>
    </motion.div>;
}